import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  Button,
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  Slideover,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  useCreateContactWebsite,
  useUpdateContactWebsite,
} from "@/lib/data/react-query";
import { ContactWebsiteSchema } from "@/lib/data/schemas";
import { CONTACT_WEBSITE_LABELS } from "@/lib/data/static/contactStatics";
import { type ContactWebsite } from "@/lib/types";
import { ContactWebsiteIcon } from "@/lib/utils/iconUtils";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import type { z } from "zod";

interface AddOrEditContactWebsiteSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
  data?: ContactWebsite;
  contactId: string;
  onDelete?: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  variant?: "slideover" | "inline";
}

type ConditionalProps =
  | {
      editing: true;
      activeContactWebsite: Pick<ContactWebsite, "id" | "dateModified">;
    }
  | {
      editing: false;
      activeContactWebsite?: never;
    };

const formSchema = ContactWebsiteSchema.omit({
  firmId: true,
  dateCreated: true,
  contactId: true,
  id: true,
});

const defaultObject: z.input<typeof formSchema> = {
  address: "",
  description: "",
  label: "",
  dateModified: "",
};

export const AddOrEditContactWebsiteSlideover = ({
  activeContactWebsite,
  contactId,
  editing,
  hasDeletePermission,
  hasEditPermission,
  onDelete,
  open,
  parentForm,
  setOpen,
  variant = "slideover",
}: AddOrEditContactWebsiteSlideoverProps & ConditionalProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? { ...defaultObject, ...activeContactWebsite }
      : defaultObject,
    mode: "onSubmit",
  });
  const createContactWebsite = useCreateContactWebsite();
  const updateContactWebsite = useUpdateContactWebsite();
  const isFormLoading =
    createContactWebsite.isPending || updateContactWebsite.isPending;

  const onSubmitParentForm = (data: z.infer<typeof formSchema>) => {
    if (!parentForm) return;
    if (editing) {
      const existingWebsites = parentForm.getValues("websites") ?? [];
      parentForm.setValue(
        "websites",
        existingWebsites.map((website) =>
          website.id === activeContactWebsite.id
            ? { ...website, ...data }
            : website,
        ),
        { shouldDirty: true },
      );
      return data;
    } else {
      const existingWebsites = parentForm.getValues("websites") ?? [];
      parentForm.setValue(
        "websites",
        [...existingWebsites, { ...data, id: Math.random().toString() }],
        { shouldDirty: true },
      );
      return data;
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (parentForm) {
      onSubmitParentForm(data);
      setOpen(false);
      return;
    }
    if (editing) {
      const dirtyKeys = Object.keys(form.formState.dirtyFields) as Array<
        keyof z.infer<typeof formSchema>
      >;
      const dirtyData: Partial<z.infer<typeof formSchema>> = {};
      for (const key of dirtyKeys) {
        (dirtyData as Record<string, unknown>)[key] = data[key];
      }
      await updateContactWebsite.mutateAsync({
        contactId,
        id: activeContactWebsite.id,
        ...dirtyData,
        dateModified: activeContactWebsite.dateModified,
      });
    } else {
      await createContactWebsite
        .mutateAsync({
          contactId,
          ...data,
          dateModified: undefined,
        })
        .then(() => {
          form.reset();
        });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const description = editing
    ? "Update the contact website details below"
    : "Create a new website for this contact";
  const title = editing ? "Update website" : "Create website";
  const isInline = variant === "inline";
  const fieldsetClassName = cn(
    slideoverFieldsetClassName,
    isInline && "p-0 my-2 gap-3",
  );
  const handleInlineSubmit = () => {
    void form.handleSubmit(onSubmit)();
  };
  const formContent = (
    <Form {...form}>
      <form
        className={cn(
          "relative flex grow flex-col",
          isInline ? "overflow-visible" : "overflow-hidden",
        )}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit(onSubmit)();
        }}
      >
        {isFormLoading && <FormLoadingOverlay />}
        <div
          className={cn(
            "overflow-y-auto",
            variant === "inline" && "overflow-visible",
          )}
        >
          <fieldset
            disabled={editing ? !hasEditPermission : false}
            className={fieldsetClassName}
          >
            <SimpleSelectFormField
              placeholder="Select a label"
              label="Label"
              register={form.register("label")}
              items={CONTACT_WEBSITE_LABELS}
            />
            <TextFormField
              register={form.register("address")}
              label="Website address*"
            />
            <div className="col-span-2">
              <TextAreaFormField
                register={form.register("description")}
                label="Description"
              />
            </div>
          </fieldset>
        </div>
        <div className="flex grow" />
        <SheetFooter addSlideoverStyles={variant === "slideover"}>
          {hasDeletePermission && editing && (
            <Button type="button" variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          )}
          <div className="flex grow justify-end gap-x-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              type={isInline ? "button" : "submit"}
              submitFn={isInline ? handleInlineSubmit : undefined}
            />
          </div>
        </SheetFooter>
      </form>
    </Form>
  );

  if (variant === "inline") {
    if (!open) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <ContactWebsiteIcon className="h-5 w-5 text-gray-400" />
          <div className="space-y-1">
            <Typography weight="medium">{title}</Typography>
            <Typography variant="subHeading" className="text-muted-foreground">
              {description}
            </Typography>
          </div>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <Slideover
      open={open}
      setOpen={setOpen}
      isDirty={form.formState.isDirty}
      title={title}
      titleIcon={<ContactWebsiteIcon className="h-4 w-4" />}
      description={description}
      isLoading={isFormLoading}
    >
      {formContent}
    </Slideover>
  );
};
